import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface ProgressiveImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholderColor?: string;
}

const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  src,
  alt,
  className = '',
  placeholderColor = '#2A2A2A'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  useEffect(() => {
    const loadImage = () => {
      const img = new Image();
      img.src = src;
      
      img.onload = () => {
        setIsLoaded(true);
        setError(false);
      };
      
      img.onerror = () => {
        if (retryCount < maxRetries) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            loadImage();
          }, Math.pow(2, retryCount) * 1000); // Exponential backoff
        } else {
          setError(true);
        }
      };
    };

    loadImage();
  }, [src, retryCount]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Placeholder */}
      <div
        className={`absolute inset-0 ${className}`}
        style={{ backgroundColor: placeholderColor }}
      />

      {/* Actual Image */}
      {!error && (
        <motion.img
          src={src}
          alt={alt}
          className={`${className} absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          initial={false}
          loading="lazy"
        />
      )}

      {/* Error State */}
      {error && (
        <div className={`absolute inset-0 flex items-center justify-center ${className} bg-background-secondary text-content-secondary text-sm`}>
          Unable to load image
        </div>
      )}
    </div>
  );
};

export default ProgressiveImage;